import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import useDashboardSelector from '../../../../../../redux/selectorHooks/useDashboardSelector';
import { useAnalytics } from '../../../../../../utils/analytics/useAnalytics';
import { assertNotNil } from '../../../../../../utils/types';
import printGraphqlErrors from '../../../../../../utils/apollo/printGraphqlErrors';
import { dashboardUpdated } from '../../../../../../utils/analytics/events/dashboard/events';
import toastMessage from '../../../../../../utils/toastMessage';
import { appRoutes } from '../../../../../../path';
import useDashboardSchemaId from '../../../../utils/useDashboardSchemaId';
import {
  IDashboardExecuteMutation,
  IDashboardExecuteMutationVariables,
} from '../../../../../../utils/apollo/graphqlSchema';
import { DASHBOARD_EXECUTE } from '../../../../../../utils/apollo/dashboards';
import useDashboardReload from '../../../../utils/useDashboardReload';
import prepareDashboardExecuteInput from './prepareDashboardExecuteInput';

const useExecuteDashboard = () => {
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const [executeDashboard, { loading: dashboardExecuting }] = useMutation<
    IDashboardExecuteMutation,
    IDashboardExecuteMutationVariables
  >(DASHBOARD_EXECUTE);

  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const instanceId = dashboard.id;

  const schemaId = useDashboardSchemaId();
  const draftCustomization = useDashboardSelector('draftCustomization');
  const reloadDashboard = useDashboardReload(dashboard.baseId);

  const handleApplyCustomizations = useCallback(async () => {
    const { data } = await executeDashboard({
      variables: {
        input: prepareDashboardExecuteInput({
          draftCustomization,
          schemaId,
          instanceId,
        }),
      },
    });
    assertNotNil(data);
    const { dashboard: executedDashboard, errors } = data.dashboardExecute;

    if (printGraphqlErrors(errors)) {
      await reloadDashboard();
      return;
    }

    assertNotNil(executedDashboard);
    track(dashboardUpdated(executedDashboard));

    toastMessage({
      message: 'Dashboard filter applied to all charts.',
      type: 'info',
    });

    navigate(
      appRoutes.dashboard.generateLink(
        executedDashboard.baseId,
        executedDashboard.id,
      ),
    );
  }, [
    draftCustomization,
    executeDashboard,
    instanceId,
    navigate,
    reloadDashboard,
    schemaId,
    track,
  ]);

  return {
    dashboardExecuting,
    handleApplyCustomizations,
  };
};

export default useExecuteDashboard;
